<template>
  <div class="flex flex-col gap-1.5 w-full" style="padding-bottom: 50px;">
    <!-- No Search Results -->
    <div v-if="search.length && !searchResults?.length" style="height: 400px"
    class="flex-grow w-full flex flex-col items-center justify-center">
      <div class="flex flex-col items-center justify-center">
        <NoSearchResultsIcon/>
        <BaseText type="label" size="sm" class="text-white mt-4">
          No results found
        </BaseText>
        <BaseText type="body" size="sm" class="text-neutral-alpha-650 mt-1">
          Try refining your search
        </BaseText>
      </div>
    </div>
    <!-- No Lens Accounts -->
    <div v-else-if="!searchResults?.length" class="no-lens-accounts w-full flex flex-col gap-1 mt-2 p-1 bg-neutral-alpha-200" style="border-radius: 10px;">
      <div class="w-full flex flex-col gap-1 p-3 text-center">
        <BaseText type="label" size="sm" class="text-white">
          No Lens Accounts
        </BaseText>
        <BaseText type="body" size="sm" class="text-neutral-alpha-650">
          Connect an ad account to build performance creative reports.
        </BaseText>
      </div>
      <button class="create-lens w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md"
      @click="openLensCreationModal">
        <LensIcon class="text-white" />
        <BaseText type="label" size="sm" class="text-white">
          Create Lens
        </BaseText>
      </button>
    </div>
    <component
      v-for="lens in searchResults" :key="`lens-account-${lens.id}`"
      :is="isLensLoading(lens) ? 'div' : 'router-link'"
      :to="{ name: 'LensDashboardView', params: { lensId: lens.id } }"
      class="group w-full flex items-center pl-1.5 pr-1 gap-2.5 rounded-md transition-colors duration-100 hover:bg-neutral-alpha-25"
      :class="{'cursor-default opacity-50': isLensLoading(lens)}"
    >
      <!-- Lens Avatar -->
      <LensAvatar :avatarSrc="lens.avatar" :lensName="lens.name" :width="32" :height="32" />
      <!-- Lens Name and Update Status -->
      <div class="flex-grow flex flex-col py-1 min-w-0 text-left">
        <!-- Name -->
        <BaseText type="label" size="sm" class="text-neutral-alpha-800 truncate">
          {{ lens.name }}
        </BaseText>
        <!-- Update Status -->
        <div class="relative">
          <BaseText type="body" size="xs" class="text-neutral-alpha-600"
          :class="{'transition-opacity duration-100 group-hover:opacity-0': isLensLoading(lens)}">
            Last updated {{ getUpdatedAgoTime(lens.last_refreshed_at) }} ago
          </BaseText>
          <BaseText v-if="isLensLoading(lens)" type="body" size="xs" class="text-neutral-alpha-600 absolute left-0 top-0
          transition-opacity duration-100 opacity-0 group-hover:opacity-100">
            Data import in progress...
          </BaseText>
        </div>
      </div>
      <RefreshIcon v-if="isLensLoading(lens)" class="text-neutral-alpha-600 mr-0.5 flex-shrink-0" />
    </component>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import { getTimeAgoShorthand } from '../../utils/dateUtils'
import LensAvatar from './LensAvatar.vue'

import LensIcon from '../globals/Icons/SidebarIcons/LensIcon.vue'
import NoSearchResultsIcon from '../globals/Icons/SidebarIcons/NoSearchResultsIcon.vue'
import RefreshIcon from '../globals/Icons/LensIcons/RefreshIcon.vue'

export default {
  name: 'LensAccounts',
  components: {
    LensAvatar,
    LensIcon,
    NoSearchResultsIcon,
    RefreshIcon
  },
  props: {
    lenses: {
      type: Array,
      default: () => []
    },
    search: {
      type: String,
      default: ''
    }
  },
  computed: {
    searchResults () {
      let searchResults = []
      this.lenses.forEach(lens => {
        if (lens.name.toLowerCase().trim().includes(this.search.toLowerCase().trim())) {
          searchResults.push(lens)
        }
      })
    
      // Sort by closeness of match
      return searchResults.sort((a, b) => {
        const ratioA = this.search.length / a.name.length
        const ratioB = this.search.length / b.name.length
        return ratioB - ratioA
      })
    }
  },
  methods: {
    ...mapMutations('LensModule', ['SET_SHOW_LENS_CREATION_MODAL']),
    getUpdatedAgoTime(dateStr) {
      if (!dateStr) return '--'
      const date = new Date(dateStr)
      return getTimeAgoShorthand(date)
    },
    openLensCreationModal () {
      this.SET_SHOW_LENS_CREATION_MODAL(true)
    },
    isLensLoading (lens) {
      return !lens.isDataReady
    }
  }
}
</script>

<style scoped>
@property --lens-grad-opacity-1 {
  syntax: '<number>';
  initial-value: 0.08;
  inherits: false;
}
@property --lens-grad-opacity-2 {
  syntax: '<number>';
  initial-value: 0.14;
  inherits: false;
}
.create-lens {
  background: linear-gradient(180deg, rgba(223, 225, 231, var(--lens-grad-opacity-1)) 0%, rgba(223, 225, 231, var(--lens-grad-opacity-1)) 100%), rgba(255, 255, 255, var(--lens-grad-opacity-2));
  transition: --lens-grad-opacity-1 100ms ease-in-out, --lens-grad-opacity-2 100ms ease-in-out;
}
.create-lens:hover {
  --lens-grad-opacity-1: 0.10;
  --lens-grad-opacity-2: 0.16;
}
</style>